import React from 'react';
import GrossYieldBedroomCard from './GrossYieldBedroomCard';
import InvestorInsightsCard from './InvestorInsightsCard';
import MarketBenchmarksCard from './MarketBenchmarksCard';
import OpportunityMatrixCard from './OpportunityMatrixCard';
import RentalBedroomAnalysisCard from './RentalBedroomAnalysisCard';
import RentalPropertyTypeAnalysisCard from './RentalPropertyTypeAnalysisCard';
import SaleBedroomAnalysisCard from './SaleBedroomAnalysisCard';
import SalePropertyTypeAnalysisCard from './SalePropertyTypeAnalysisCard';

type MarketSegment = {
  average?: number | null;
  median?: number | null;
  min?: number | null;
  max?: number | null;
  averagePerSqFt?: number | null;
  medianPerSqFt?: number | null;
  averageSquareFootage?: number | null;
  medianSquareFootage?: number | null;
  averageDaysOnMarket?: number | null;
  medianDaysOnMarket?: number | null;
  minDaysOnMarket?: number | null;
  maxDaysOnMarket?: number | null;
  newListings?: number | null;
  totalListings?: number | null;
};

type BreakdownRow = MarketSegment & {
  propertyType?: string;
  bedrooms?: number | string;
};

type HistoryRow = {
  date?: string;
  average?: number | null;
  median?: number | null;
  totalListings?: number | null;
  newListings?: number | null;
};

export type RentcastMarketStatistics = {
  zipCode?: string;
  lastUpdated?: string | null;
  sale?: MarketSegment | null;
  rental?: MarketSegment | null;
  saleByPropertyType?: BreakdownRow[];
  rentalByPropertyType?: BreakdownRow[];
  saleByBedrooms?: BreakdownRow[];
  rentalByBedrooms?: BreakdownRow[];
  saleHistory?: HistoryRow[];
  rentalHistory?: HistoryRow[];
};

const money = (n: number | null | undefined) =>
  n != null && Number.isFinite(Number(n)) ? `$${Math.round(Number(n)).toLocaleString()}` : '—';

const num = (n: number | null | undefined, suffix = '') =>
  n != null && Number.isFinite(Number(n)) ? `${Number(n).toLocaleString()}${suffix}` : '—';

const HistoryTable: React.FC<{ title: string; rows?: HistoryRow[]; rentMode?: boolean }> = ({
  title,
  rows,
  rentMode,
}) => {
  if (!rows?.length) return null;
  const recent = [...rows].slice(-6).reverse();
  return (
    <div style={{ marginBottom: 14 }}>
      <h4 style={{ fontSize: 13, marginBottom: 6 }}>{title}</h4>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '4px 6px' }}>Month</th>
              <th style={{ padding: '4px 6px' }}>Median</th>
              <th style={{ padding: '4px 6px' }}>Average</th>
              <th style={{ padding: '4px 6px' }}>Listings</th>
              <th style={{ padding: '4px 6px' }}>New</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((row, i) => (
              <tr key={`hist-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '4px 6px' }}>{row.date ? String(row.date).slice(0, 7) : '—'}</td>
                <td style={{ padding: '4px 6px' }}>{rentMode ? `${money(row.median)}/mo` : money(row.median)}</td>
                <td style={{ padding: '4px 6px' }}>{rentMode ? `${money(row.average)}/mo` : money(row.average)}</td>
                <td style={{ padding: '4px 6px' }}>{num(row.totalListings)}</td>
                <td style={{ padding: '4px 6px' }}>{num(row.newListings)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RentcastMarketPanel: React.FC<{ market?: RentcastMarketStatistics | null }> = ({ market }) => {
  if (!market) return null;
  return (
    <div>
      <RentalBedroomAnalysisCard rows={market.rentalByBedrooms} />
      <SaleBedroomAnalysisCard rows={market.saleByBedrooms} />
      <RentalPropertyTypeAnalysisCard rows={market.rentalByPropertyType} />
      <SalePropertyTypeAnalysisCard rows={market.saleByPropertyType} />
      <GrossYieldBedroomCard
        saleRows={market.saleByBedrooms}
        rentalRows={market.rentalByBedrooms}
      />
      <OpportunityMatrixCard
        saleRows={market.saleByBedrooms}
        rentalRows={market.rentalByBedrooms}
      />
      <MarketBenchmarksCard sale={market.sale} />
      <InvestorInsightsCard market={market} />
      <HistoryTable title="Sale price history (monthly)" rows={market.saleHistory} />
      <HistoryTable title="Rental history (monthly)" rows={market.rentalHistory} rentMode />
    </div>
  );
};

export default RentcastMarketPanel;
